import { useCallback, useMemo } from 'react'
import { FC } from 'react'
import * as styles from 'components/features/pfm/PageParamsHeader/PageParamsStickyHeader.styl'
import { TimeframedPage } from 'interfaces/pages'
import { PAGE_DEFINITIONS_MAP } from 'constants/pages'
import { PageHeading } from 'components/atoms/Text'
import { TimeframeSelectContainer } from 'components/features/pfm/PageParamsHeader/TimeframeSelectContainer'
import { isCategorizedPage } from 'helpers/pageHelpers'
import withMobile, { WithMobileProps } from 'hocs/withMobile/withMobile'
import { CategorySelect } from 'components/features/pfm/PageParamsHeader/CategorySelect'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { ModifyFloatingPaneAction } from 'actions/floatingPanesActions'
import { createPluralFormatter } from 'helpers/formatHelpers'
import { useEnabledAccounts } from 'components/features/pageFilters/useEnabledAccounts'
import { createTopPaneSelector } from 'selectors/floatingPanesSelectors'
import cn from 'classnames'
import { cssVar } from 'helpers/styleHelpers'

const accountsPlural = createPluralFormatter('Account', 'Accounts')

type PageParamsStickyHeaderProps =
  & WithMobileProps
  & {
    type: TimeframedPage
  }

const PageParamsStickyHeaderComponent: FC<PageParamsStickyHeaderProps> = (props) => {
  const { type, isMobile } = props

  const categoryDropdownChild = useMemo(() => {
    if (!isCategorizedPage(type)) {
      return null
    }

    return (
      <CategorySelect
        type={ type }
        className={ styles.timeframeDropdown }
        captionClassName={ styles.dropdownCaption }
        withArrow={ !isMobile }
      />
    )
  }, [isMobile, type])

  const enabledAccounts = useEnabledAccounts()


  const topPaneSelector = useMemo(createTopPaneSelector, [])
  const topPane = useStoreSelector(topPaneSelector)
  const isPageFilterExpanded = useMemo(() => {
    return topPane?.type === 'page-filter' && topPane.mode === 'by-account'
  }, [topPane])

  const dispatch = useStoreDispatch()
  const handleClick = useCallback(() => {
    dispatch(new ModifyFloatingPaneAction({
      type: 'page-filter',
      mode: 'by-account',
      page: 'dashboard',
    }))
  }, [dispatch])

  const headingChild = useMemo(() => {
    if (type === 'future') {
      return (
        <>
          Your&nbsp;<TimeframeSelectContainer type={ type } />
        </>
      )
    }

    return (
      <>
        Your&nbsp;
        { PAGE_DEFINITIONS_MAP[type].title }
        { !!categoryDropdownChild && (
          <>
            { ' ' }
            in
            { ' ' }
            { categoryDropdownChild }
          </>
        )}
        { ' ' }
        in the <TimeframeSelectContainer type={ type } className={ styles.timeframeDropdown } captionClassName={ styles.dropdownCaption } />
        { enabledAccounts.count !== 0 && type !== 'creditscore' && (
          <>
            { ' ' }
            in
            { ' ' }
            <a
              onClick={ handleClick }
              className={ cn(styles.accountsFilterButton, isPageFilterExpanded && styles.expanded) }
              data-qa-marker='accounts-filtering'
              data-qa-type='button'
            >
              <span data-qa-marker='enabled-accounts' data-qa-type='title'>
                { enabledAccounts.title }
              </span>
              <i className={ styles.arrow } />
            </a>
          </>
        )}
      </>
    )
  }, [categoryDropdownChild, enabledAccounts.count, enabledAccounts.title, handleClick, isPageFilterExpanded, type])

  const handleOpenFuturePane = useCallback((mode) => {
    dispatch(new ModifyFloatingPaneAction({
      type: 'future',
      mode,
    }))
  }, [dispatch])

  const futureButtonsChild = useMemo(() => {
    if (type !== 'future') {
      return null
    }

    return (
      <>
        <div className={ styles.assumptionButtonSection } key='assumptions'>
          <div className={ styles.dropdownLabel }>Assumptions</div>
          <div className={ styles.dropdownWrapper }>
            <button
              className={ styles.futureButton }
              onClick={ () => handleOpenFuturePane('assumptions') }
            >
              Adjust
            </button>
          </div>
        </div>
        <div className={ styles.layerButtonSection } key='events'>
          <div className={ styles.dropdownLabel }>Goals &amp; Events</div>
          <div className={ styles.dropdownWrapper }>
            <button
              className={ styles.futureButton }
              onClick={ () => handleOpenFuturePane('all-layers') }
            >
              Add
            </button>
          </div>
        </div>
      </>
    )
  }, [handleOpenFuturePane, type])

  const handleAccountsClick = useCallback(() => {
    if (type === 'future') {
      return
    }

    dispatch(new ModifyFloatingPaneAction({
      type: 'page-filter',
      mode: 'by-account',
      page: type,
    }))
  }, [dispatch, type])

  const accountsButtonChild = useMemo(() => {
    if (enabledAccounts.count === 0 || type === 'future' || type === 'transactions' || type === 'creditscore') {
      return null
    }

    return (
      <div className={ styles.accountsButtonSection } key='accounts'>
        <div className={ styles.dropdownLabel }>
          { accountsPlural(enabledAccounts.count) }
        </div>
        <div className={ styles.dropdownWrapper }>
          <a
            className={ styles.futureButton }
            onClick={ handleAccountsClick }
          >
            { enabledAccounts.title }
          </a>
        </div>
      </div>
    )
  }, [enabledAccounts.count, enabledAccounts.title, handleAccountsClick, type])

  const mobileCategoryChild = useMemo(() => {
    if (!categoryDropdownChild) {
      return null
    }

    return (
      <div className={ styles.categorySection } key='category'>
        <div className={ styles.dropdownLabel }>
          Category
        </div>
        <div className={ styles.dropdownWrapper }>
          { categoryDropdownChild }
        </div>
      </div>
    )
  }, [categoryDropdownChild])

  const mobileChild = useMemo(() => {
    return [
      mobileCategoryChild,
      (
        <div className={ styles.timeframeSection } key='timeframe'>
          <div className={ styles.dropdownLabel }>Timeframe</div>
          <div className={ styles.dropdownWrapper }>
            <TimeframeSelectContainer type={ type } className={ styles.timeframeDropdown } withArrow={ !isMobile } captionClassName={ styles.dropdownCaption } />
          </div>
        </div>
      ),
      futureButtonsChild,
      accountsButtonChild,
    ].filter((item): item is NonNullable<typeof item> => {
      return item !== null
    })
  }, [accountsButtonChild, futureButtonsChild, isMobile, mobileCategoryChild, type])

  return useMemo(() => {
    if (isMobile) {
      return (
        <div
          className={ styles.header }
          style={ cssVar({
            '--items-count': `${ mobileChild.length }`,
          }) }
        >
          { mobileChild }
        </div>
      )
    }

    return (
      <div className={ styles.header }>
        <PageHeading className={ styles.title } qaMarker='sticky-heading'>
          { headingChild }
        </PageHeading>
      </div>
    )
  }, [headingChild, isMobile, mobileChild])
}

export const PageParamsStickyHeader = withMobile(PageParamsStickyHeaderComponent)
